/**
 * The one rule for a publish target whose provider outcome is unknown. Pure (no database, no Deno), so the editor,
 * the edge functions and vitest share it. The database side is `supabase/functions/_shared/provider-outcome.ts`.
 *
 * THE RULE. A provider call was made and its result is not known, so the post may be live.
 *
 *   native_status_unknown        A job id is stored and a provider check runs. Never sent again automatically.
 *   manual_action_required       Nothing will check it any more (no id, no check for this provider, or the check
 *   + check_platform code        window ended). Never sent again automatically. The author has to look.
 *
 * A target leaves those states in three ways only: a provider check settles it, an expired check hands it to the
 * author, or, once it waits on the author, they say "I checked, post again" and the provider does not say otherwise.
 * Neither state is in RECLAIMABLE_DELIVERY_STATUSES, whatever
 * its age and whether or not an id is stored.
 */

/** A target's delivery states that a new attempt may claim. Only states where nothing may be live. */
export const RECLAIMABLE_DELIVERY_STATUSES = ['unscheduled', 'scheduled', 'failed'] as const;

export const OUTCOME_UNKNOWN_CODE = 'provider_outcome_unknown';
export const CHECK_PLATFORM_CODE = 'check_platform_before_reposting';

/** Providers Mora can ask about a stored job id. */
export const PROVIDERS_WITH_A_CHECK: readonly string[] = ['instagram', 'tiktok', 'linkedin'];

/** How long a provider check keeps asking, from the attempt's start. Instagram containers expire at 24 hours. */
export const OUTCOME_CHECK_WINDOW_MS = 24 * 60 * 60_000;

/** A target left in `publishing` this long lost its worker: the call may have been made. */
export const STALE_PUBLISHING_MS = 15 * 60_000;

const LABELS: Record<string, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
};

function label(platform: string): string {
  return LABELS[platform] ?? 'the platform';
}

/** "TikTok", "TikTok and LinkedIn", "TikTok, LinkedIn and Instagram". */
export function platformNames(platforms: readonly string[]): string {
  const names = platforms.map(label);
  if (names.length <= 1) return names[0] ?? 'the platform';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** True while Mora really is asking the provider. */
export function stillBeingCheckedMessage(platform: string): string {
  return `Still being checked with ${label(platform)}. This post will not be sent again while that runs.`;
}

/** True once nothing will check it. */
export function checkPlatformMessage(platform: string): string {
  const name = label(platform);
  return `Mora could not confirm this post on ${name}. Check ${name}, then post again only if it is not there.`;
}

export const NOT_ATTEMPTED_MESSAGE =
  "Nothing was sent, because Mora could not read this post's delivery state. It is safe to try again.";

export const ALREADY_SENDING_MESSAGE = 'This post is already being sent. Nothing new was sent.';

/** The refusal when a platform with an unknown outcome is asked for without the author's word for that platform. */
export function repostNeedsConfirmationMessage(platforms: readonly string[]): string {
  const names = platformNames(platforms);
  return `Mora does not know yet whether this post went out on ${names}. Check ${names} first, then choose "I checked, post again".`;
}

/** A check can still end in a live post, so posting again is not available yet. */
export function repostWhileCheckingMessage(platforms: readonly string[]): string {
  return `Mora is still checking ${platformNames(platforms)}. You can post again once that check ends. Nothing was sent.`;
}

export function repostAlreadyLiveMessage(platform: string): string {
  return `${label(platform)} says this post is live. Nothing was sent.`;
}

export function repostStillRunningMessage(platform: string): string {
  return `${label(platform)} is still working on this post, so it could still go live. Nothing was sent. Try again once it finishes.`;
}

export function repostStatusUnavailableMessage(platform: string): string {
  return `Mora could not ask ${label(platform)} about this post just now. Nothing was sent. Try again shortly.`;
}

export const SEND_INTERRUPTED_MESSAGE =
  'This send stopped before it reached the platform. It is safe to try again.';

export const REPOST_CONFIRMATION_WARNING =
  'If the first post did go out, or goes out later, this makes a duplicate. Post again only if it is not on the platform.';

export interface OutcomeFields {
  delivery_status: 'native_status_unknown' | 'manual_action_required';
  error_code: string;
  error_message: string;
}

/** The target's fields after an unknown outcome: checked if there is something to check with, else the author's. */
export function unknownOutcomeFields(
  platform: string,
  providerPublishId: string | null | undefined,
): OutcomeFields {
  if (providerPublishId && PROVIDERS_WITH_A_CHECK.includes(platform)) {
    return {
      delivery_status: 'native_status_unknown',
      error_code: OUTCOME_UNKNOWN_CODE,
      error_message: stillBeingCheckedMessage(platform),
    };
  }
  return needsAuthorFields(platform);
}

export function needsAuthorFields(platform: string): OutcomeFields {
  return {
    delivery_status: 'manual_action_required',
    error_code: CHECK_PLATFORM_CODE,
    error_message: checkPlatformMessage(platform),
  };
}

export interface OutcomeTargetRow {
  delivery_status?: string | null;
  error_code?: string | null;
}

/** The post may be live and only a check or the author can say. Includes a check that ended. */
export function needsOutcomeResolution(row: OutcomeTargetRow | null | undefined): boolean {
  if (!row) return false;
  if (row.delivery_status === 'native_status_unknown') return true;
  return row.delivery_status === 'manual_action_required' && row.error_code === CHECK_PLATFORM_CODE;
}

/**
 * "I checked, post again" is offered and accepted only here: the check has ended with no answer and the target waits on
 * the author. While a check runs it can still end in a live post, so it is refused for every provider.
 */
export function repostAvailable(row: OutcomeTargetRow | null | undefined): boolean {
  return (
    row?.delivery_status === 'manual_action_required' && row.error_code === CHECK_PLATFORM_CODE
  );
}

/** Why this target may not be sent now, or null when a new attempt may try to claim it. */
export function resendRefusal(
  platform: string,
  row: OutcomeTargetRow | null | undefined,
): string | null {
  if (!row) return null;
  if (row.delivery_status === 'native_status_unknown') return stillBeingCheckedMessage(platform);
  if (row.delivery_status === 'manual_action_required') {
    return row.error_code === CHECK_PLATFORM_CODE
      ? checkPlatformMessage(platform)
      : (row.error_code ?? 'This post needs a step from you before it can be sent.');
  }
  if (row.delivery_status === 'publishing') return ALREADY_SENDING_MESSAGE;
  return null;
}

/** A check's window runs from the attempt's start, so a post scheduled days ahead is checked like any other. */
export function checkWindowExpired(
  attemptStartedAt: string | null | undefined,
  nowMs: number,
): boolean {
  const started = Date.parse(attemptStartedAt ?? '');
  return !Number.isFinite(started) || nowMs - started >= OUTCOME_CHECK_WINDOW_MS;
}

export interface ParentRollup {
  finalStatus: string;
  /**
   * The `status` column. An unknown outcome maps to `publishing`, not `scheduled`: `claim_due_posts` claims due
   * `scheduled` rows, so `scheduled` would hand the post back to the cron on every run. The exception is a sibling
   * that is still owed a send: the row must be claimed for that sibling, and the unknown target refuses by itself.
   */
  legacyStatus: string;
  statusRollup: string;
}

export interface RollupRow {
  /** Staggered rows are claimed per platform, so their `status` never has to be `scheduled` to be claimed. */
  platformSchedules?: unknown;
  scheduledAt?: string | null;
}

/**
 * The parent post's status from its delivery states. The only copy of this rule; every writer goes through it.
 * `unscheduled` means owed a send with no time: it is not claimed, so it never holds the row in `scheduled`.
 */
export function parentRollupFromTargetStates(
  states: readonly string[],
  row: RollupRow = {},
): ParentRollup {
  const published = ['published', 'published_with_warnings'];
  const allPublished = states.length > 0 && states.every((s) => published.includes(s));
  const anyPublished = states.some((s) => published.includes(s));
  const anySending = states.includes('publishing');
  const staggered = Boolean(row.platformSchedules);
  const hasTime = staggered || row.scheduledAt === undefined || Boolean(row.scheduledAt);
  const owedAtATime = hasTime && states.includes('scheduled');
  const anyUnknown = states.includes('native_status_unknown');
  const anyManual = states.includes('manual_action_required');
  const finalStatus = allPublished
    ? 'published'
    : anyPublished && (owedAtATime || anySending)
      ? 'partially_scheduled'
      : anyPublished
        ? 'partially_published'
        : anySending
          ? 'publishing'
          : anyUnknown
            ? 'native_status_unknown'
            : anyManual
              ? 'manual_action_required'
              : owedAtATime
                ? 'scheduled'
                : 'failed';
  const legacyStatus =
    owedAtATime && !staggered
      ? 'scheduled'
      : finalStatus === 'native_status_unknown'
        ? 'publishing'
        : finalStatus === 'manual_action_required'
          ? 'failed'
          : finalStatus;
  return {
    finalStatus,
    legacyStatus,
    statusRollup:
      finalStatus === 'partially_published' || finalStatus === 'partially_scheduled'
        ? 'published_partial'
        : finalStatus,
  };
}
